import React, { useEffect, useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { toast } from 'react-toastify';
import MainLayout from '../../components/Layout/MainLayout';
import reportsService from '../../services/reportsService';
import { exportToCSV, exportToPDF } from '../../utils/reports/exportUtils';

const COLORS = ['#22C55E', '#EF4444', '#F59E0B'];

const InterviewReportPage = () => {
  const [filters, setFilters] = useState({ fromDate: '', toDate: '', hrName: '', page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ data: [], pagination: {} });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getInterviewReport(filters);
      if (response.success) {
        setPayload(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load interview report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.fromDate, filters.toDate, filters.hrName, filters.page, filters.limit]);

  const pieData = useMemo(() => {
    const map = { Selected: 0, Rejected: 0, Pending: 0 };
    payload.data.forEach((x) => { map[x.result] = (map[x.result] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [payload.data]);

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="text-3xl font-bold text-charcoal">Interview Report</h1>
          <div className="flex gap-2">
            <button
              onClick={() => exportToCSV(
                'interview-report',
                [
                  { label: 'Candidate', key: 'candidateName' },
                  { label: 'HR', key: 'hrName' },
                  { label: 'Interview Date', key: 'interviewDate' },
                  { label: 'Result', key: 'result' },
                  { label: 'Feedback', key: 'feedback' }
                ],
                payload.data
              )}
              className="px-4 py-2 bg-olive text-white rounded-lg text-sm"
            >Export CSV</button>
            <button onClick={exportToPDF} className="px-4 py-2 bg-charcoal text-white rounded-lg text-sm">Export PDF</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <input type="date" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value, page: 1 }))} className="border rounded-lg px-3 py-2" />
          <input type="date" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value, page: 1 }))} className="border rounded-lg px-3 py-2" />
          <input type="text" placeholder="HR Name" value={filters.hrName} onChange={(e) => setFilters((p) => ({ ...p, hrName: e.target.value, page: 1 }))} className="border rounded-lg px-3 py-2" />
          <select value={filters.limit} onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))} className="border rounded-lg px-3 py-2">
            <option value={10}>10 / page</option><option value={20}>20 / page</option><option value={50}>50 / page</option>
          </select>
          <button onClick={() => setFilters({ fromDate: '', toDate: '', hrName: '', page: 1, limit: filters.limit })} className="px-4 py-2 bg-gray-100 rounded-lg">Clear</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-charcoal mb-4">Result Split</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={110} label>
                  {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50"><tr><Th>Candidate</Th><Th>HR</Th><Th>Date</Th><Th>Result</Th><Th>Feedback</Th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="px-4 py-6 text-center">Loading...</td></tr> :
                payload.data.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center">No records</td></tr> :
                payload.data.map((row) => (
                  <tr key={row._id} className="border-t">
                    <Td>{row.candidateName}</Td>
                    <Td>{(row.hrName && String(row.hrName).trim()) || 'Unknown HR'}</Td>
                    <Td>{new Date(row.interviewDate).toLocaleString()}</Td>
                    <Td>{row.result}</Td>
                    <Td>{row.feedback || '-'}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination pagination={payload.pagination} onPageChange={(nextPage) => setFilters((p) => ({ ...p, page: nextPage }))} />
      </div>
    </MainLayout>
  );
};

const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs uppercase text-gray-600">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;
const Pagination = ({ pagination, onPageChange }) => (
  <div className="flex items-center justify-between">
    <p className="text-sm text-gray-600">Page {pagination?.page || 1} of {pagination?.totalPages || 1}</p>
    <div className="flex gap-2">
      <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={!pagination?.page || pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)}>Previous</button>
      <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={!pagination?.page || pagination.page >= pagination.totalPages} onClick={() => onPageChange(pagination.page + 1)}>Next</button>
    </div>
  </div>
);

export default InterviewReportPage;
